import React, { useEffect, useState } from "react";
import { DomainService, CustomDomain } from "../services/domainService";
import { WorkspaceService } from "../services/workspaceService";
import { QRDesignTemplate, QRFolder, QRStyleOptions } from "../types";

interface Props {
  currentStyle?: QRStyleOptions;
  focus?: "workspace" | "domains";
}

const WorkspaceManager: React.FC<Props> = ({ currentStyle, focus }) => {
  const [folders, setFolders] = useState<QRFolder[]>([]);
  const [templates, setTemplates] = useState<QRDesignTemplate[]>([]);
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [folderName, setFolderName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [domain, setDomain] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    const [nextFolders, nextTemplates, nextDomains] = await Promise.all([
      WorkspaceService.getFolders(),
      WorkspaceService.getTemplates(),
      DomainService.list(),
    ]);
    setFolders(nextFolders);
    setTemplates(nextTemplates);
    setDomains(nextDomains);
  };

  useEffect(() => {
    void load().catch((error) => console.error("Workspace data failed", error));
  }, []);

  const addFolder = async () => {
    if (!folderName.trim()) return;
    await WorkspaceService.createFolder(folderName);
    setFolderName("");
    await load();
    setMessage("Ordner erstellt.");
  };

  const addTemplate = async () => {
    if (!templateName.trim() || !currentStyle) return;
    await WorkspaceService.saveTemplate(templateName, currentStyle);
    setTemplateName("");
    await load();
    setMessage("Designvorlage gespeichert.");
  };

  const verifyDomain = async () => {
    if (!domain.trim()) return;
    const result = await DomainService.verify(domain);
    setDomain("");
    await load();
    setMessage(
      result.status === "active"
        ? "Domain ist verbunden und SSL-aktiv."
        : result.status === "verified"
          ? "Besitz verifiziert. Übernimm jetzt die angezeigten Firebase-DNS-Einträge und prüfe erneut."
          : `DNS-Eintrag erforderlich: ${result.txtName} → ${result.txtValue}`,
    );
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          {message}
        </div>
      )}
      {focus !== "domains" && (
        <section className="app-panel p-5 sm:p-6">
          <div>
            <h2 className="font-semibold">Ordner</h2>
            <p className="mt-1 text-sm text-slate-500">
              Kampagnen und Kunden sauber organisieren.
            </p>
          </div>
          <div className="mt-5 flex gap-2">
            <input
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              className="app-input"
              placeholder="Neuer Ordner"
            />
            <button
              type="button"
              onClick={() => void addFolder()}
              className="app-primary-button"
            >
              Erstellen
            </button>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="flex items-center justify-between rounded-xl border border-slate-200 p-3"
              >
                <span className="inline-flex items-center gap-2 text-sm font-semibold">
                  <span
                    className="h-3 w-3 rounded"
                    style={{ background: folder.color }}
                  />
                  {folder.name}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await WorkspaceService.deleteFolder(folder.id);
                    await load();
                  }}
                  className="text-xs font-semibold text-red-600"
                >
                  Löschen
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      {focus !== "domains" && (
        <section className="app-panel p-5 sm:p-6">
          <div>
            <h2 className="font-semibold">Designvorlagen</h2>
            <p className="mt-1 text-sm text-slate-500">
              Markendesigns wiederverwenden. Eine neue Vorlage kann direkt aus
              dem Builder gespeichert werden.
            </p>
          </div>
          {currentStyle && (
            <div className="mt-5 flex gap-2">
              <input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                className="app-input"
                placeholder="Name der Vorlage"
              />
              <button
                type="button"
                onClick={() => void addTemplate()}
                className="app-primary-button"
              >
                Speichern
              </button>
            </div>
          )}
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {templates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between rounded-xl border border-slate-200 p-3"
              >
                <span className="text-sm font-semibold">{template.name}</span>
                <button
                  type="button"
                  onClick={async () => {
                    await WorkspaceService.deleteTemplate(template.id);
                    await load();
                  }}
                  className="text-xs font-semibold text-red-600"
                >
                  Löschen
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      {focus !== "workspace" && (
        <section className="app-panel p-5 sm:p-6">
          <div>
            <h2 className="font-semibold">Eigene Redirect-Domain</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Verifiziere den Besitz per TXT. Danach verbindet RedirectQR die
              Domain mit Firebase Hosting und zeigt dir die benötigten
              DNS-Einträge; SSL wird automatisch bereitgestellt.
            </p>
          </div>
          <div className="mt-5 flex gap-2">
            <input
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
              className="app-input"
              placeholder="qr.meinefirma.de"
            />
            <button
              type="button"
              onClick={() => void verifyDomain()}
              className="app-primary-button"
            >
              Prüfen
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {domains.map((item) => (
              <div
                key={item.domain}
                className="rounded-xl border border-slate-200 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-sm">{item.domain}</span>
                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${item.status === "active" ? "bg-emerald-50 text-emerald-700" : item.status === "verified" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}
                  >
                    {item.status === "active"
                      ? "SSL aktiv"
                      : item.status === "verified"
                        ? "Hosting-DNS ausstehend"
                        : "Besitz-DNS ausstehend"}
                  </span>
                </div>
                {item.status === "pending" && (
                  <p className="mt-3 break-all text-xs text-slate-500">
                    TXT: {item.txtName} = {item.txtValue}
                  </p>
                )}
                {(item.dnsRecords || []).map((record) => (
                  <p
                    key={`${record.domainName}-${record.type}-${record.rdata}`}
                    className="mt-2 break-all rounded-lg bg-slate-50 p-2 font-mono text-xs text-slate-600"
                  >
                    {record.requiredAction}: {record.type} · {record.domainName}{" "}
                    · {record.rdata}
                  </p>
                ))}
                {item.hostingError && (
                  <p className="mt-2 text-xs text-red-600">
                    {item.hostingError}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default WorkspaceManager;
